// The printable mileage log, opened in its own tab: Letter pages for the browser's Print → Save as
// PDF. Totals come first (agreed with the user), then every trip, then the disclaimer.

import { useEffect } from "react";
import { useAccount } from "../data/AccountData.tsx";
import { formatDistance, formatLongDate, formatMoney, formatRate, formatShortDate, formatTimeRange, listText } from "../domain/format.ts";
import { metersPerUnit } from "../domain/rates.ts";
import { rangeText } from "../domain/periods.ts";
import { buildReport, choiceFromQuery } from "./exportActions.ts";
import "./report.css";

export function Report({ query }: { query: URLSearchParams }) {
  const { state, reload } = useAccount();

  useEffect(() => {
    if (state.kind === "loaded") document.title = "Mileage Log";
  }, [state.kind]);

  if (state.kind === "loading") return <p className="rp-status" role="status">Preparing your mileage log…</p>;
  if (state.kind === "error") {
    return <p className="rp-status" role="alert">Couldn’t load your trips. <button type="button" onClick={reload}>Try Again</button></p>;
  }

  const data = state.data;
  const report = buildReport(data, choiceFromQuery(query, data));
  const currency = report.currencyCode;
  const unit = report.unit;
  const toMeters = metersPerUnit[unit];
  const withinOneYear = report.interval.start.getFullYear() === new Date(report.interval.end.getTime() - 1).getFullYear();

  return (
    <div className="rp">
      <div className="rp-tools">
        <button type="button" onClick={() => window.print()}>Print or Save as PDF</button>
      </div>
      <article className="rp-page">
        <header className="rp-head">
          <div>
            <h1 className="rp-round">Mileage Log</h1>
            <h2 className="rp-round">{report.periodTitle}</h2>
            <p className="rp-muted">{rangeText(report.interval)}</p>
          </div>
          <div className="rp-brand"><img src="/app-icon.png" alt="" />Carveny</div>
        </header>

        <div className="rp-top">
          <dl className="rp-facts">
            <dt>Vehicle</dt><dd>{report.vehicleName ?? "All vehicles"}</dd>
            <dt>Categories</dt><dd>{listText(report.categories.map((category) => category.name))}</dd>
            <dt>Rates</dt>
            <dd>
              {report.ratesUsed.length === 0 ? "None set" : report.ratesUsed.map((rate, index) => (
                <span key={index}>{rate.category.name}: {formatRate(rate.amountTenThousandths / 10_000, rate.currencyCode, rate.unit)} from {formatShortDate(new Date(rate.year, rate.start.month - 1, rate.start.day))}<br /></span>
              ))}
            </dd>
            <dt>Generated</dt><dd>{formatLongDate(report.generatedAt)}</dd>
          </dl>
          <table className="rp-totals">
            <thead><tr><th scope="col">Totals</th><th scope="col" className="r">Trips</th><th scope="col" className="r">Distance</th><th scope="col" className="r">Value</th></tr></thead>
            <tbody>
              {report.categoryTotals.map((total) => (
                <tr key={total.category.rawValue}>
                  <td>{total.category.name}</td>
                  <td className="r">{total.trips}</td>
                  <td className="r">{formatDistance(total.distance * toMeters, unit)}</td>
                  <td className="r">{total.value !== undefined && currency ? formatMoney(total.value, currency) : "—"}</td>
                </tr>
              ))}
              <tr className="rp-total">
                <td>Total</td>
                <td className="r">{report.rows.length}</td>
                <td className="r">{formatDistance(report.totalDistance * toMeters, unit)}</td>
                <td className="r">{report.totalValue !== undefined && currency ? formatMoney(report.totalValue, currency) : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {report.rows.length === 0 ? <p className="rp-muted" style={{ marginTop: 24 }}>No trips in this period for these categories.</p> : (
          <table className="rp-trips">
            <thead>
              <tr><th scope="col">Date</th><th scope="col">Time</th><th scope="col">Trip</th><th scope="col">Category</th><th scope="col" className="r">Distance</th><th scope="col" className="r">Value</th></tr>
            </thead>
            <tbody>
              {report.rows.map((row) => {
                const title = row.from && row.to ? (row.isRoundTrip ? `${row.from} ⇄ ${row.to} (round trip)` : `${row.from} → ${row.to}`) : row.from ?? row.to ?? "Trip";
                const sub = [row.purpose, row.vehicleName, row.notes].filter(Boolean).join(" · ");
                return (
                  <tr key={row.tripId}>
                    <td>{row.start.toLocaleDateString(undefined, withinOneYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" })}</td>
                    <td>{formatTimeRange(row.start, row.end)}</td>
                    <td>{title}{sub && <div className="rp-sub">{sub}</div>}</td>
                    <td>{row.category.name}</td>
                    <td className="r">{formatDistance(row.distance * toMeters, unit)}</td>
                    <td className="r">{row.value !== undefined && row.rate ? formatMoney(row.value, row.rate.currencyCode) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <footer className="rp-foot">
          <p>Values use the flat rates entered in Carveny and aren’t tax advice. Keep this log with your records.</p>
          <p className="rp-muted">Mileage Log · {report.periodTitle} · Generated {formatShortDate(report.generatedAt)}</p>
        </footer>
      </article>
    </div>
  );
}
